/**
 * PORTFOLIO — docs/03 §3.6.
 *
 * `GET /portfolio/history` and `GET /wallet/ledger` are **not** the same thing,
 * and the distinction is load-bearing for D2: history is *trading* activity
 * rolled up per market; the ledger is the double-entry money ledger where every
 * balance change has a cause. "Where did my money go" is answered by the second
 * one, and it must reconcile to the penny against `/portfolio/balance`.
 *
 * Balance has **four** components since §F26l and the client renders all four
 * separately — `available`, `order_locked`, `locked_in_positions`,
 * `pending_settlement` — plus `equity`. Those four sum to `equity` exactly, in
 * integer micro-USDC. `position_value` and `unrealized_pnl` are **forecasts and
 * are not in the identity**: equity is cash, a mark is an opinion, and putting
 * one inside the other makes equity move when nothing happened.
 *
 * `pending_withdrawal` and `withdrawable` are **gone**. Both were custodial, and
 * there is no withdrawal when the money never left the user's wallet. `equity`
 * is now a reconstruction that reconciles against the chain, not a ledger
 * identity — which is what Phase 8's exit criterion 3 asserts.
 *
 * `order_locked` is **reserved, not locked**, and is labelled that way wherever
 * it renders. It is the one component with no on-chain source: the USDC sits in
 * the user's own wallet and they can ask to unwrap it, at which point it leaves
 * the balance and the fill cannot happen.
 *
 * During the roll `pending_settlement` moves **twice in two seconds**, and
 * conflating it with `available` is the single most common cause of "my balance
 * went down" tickets. There is deliberately no summed-balance helper in this
 * codebase.
 *
 * Balance and positions are `Snapshot-of` endpoints: cold load, gap recovery
 * and reconnect only. **Never polled.**
 */

import type { HttpClient, RequestOptions } from '../client'
import type { OkBody, Page, OpQuery } from '../types'

export function portfolioEndpoints(client: HttpClient) {
  return {
    getBalance(options?: RequestOptions) {
      return client.get<OkBody<'getBalance'>>('/portfolio/balance', options)
    },

    listPositions(query?: OpQuery<'listPositions'>, options?: RequestOptions) {
      return client.get<Page<OkBody<'listPositions'>>>('/portfolio/positions', { ...options, query })
    },

    /** Only for a market **not** in the cold-load list — a deep link into a
     *  historical market where the user held a position. The live ticket reads
     *  its position from the list, in memory. */
    getPosition(marketId: string, options?: RequestOptions) {
      return client.get<OkBody<'getPosition'>>(
        `/portfolio/positions/${encodeURIComponent(marketId)}`,
        options,
      )
    },

    /** Settled tab, and the "while you were away" reconciliation after a long
     *  disconnect, with `since_ms` (§9.2.5). */
    listSettlements(query?: OpQuery<'listSettlements'>, options?: RequestOptions) {
      return client.get<Page<OkBody<'listSettlements'>>>('/portfolio/settlements', { ...options, query })
    },

    /** Recomputed **client-side** from `settlements` and `balance` events
     *  between fetches; re-fetched at most once per 30s and only while the
     *  Portfolio screen is visible. */
    getPnl(query?: OpQuery<'getPnl'>, options?: RequestOptions) {
      return client.get<OkBody<'getPnl'>>('/portfolio/pnl', { ...options, query })
    },

    getPortfolioHistory(query?: OpQuery<'getPortfolioHistory'>, options?: RequestOptions) {
      return client.get<Page<OkBody<'getPortfolioHistory'>>>('/portfolio/history', { ...options, query })
    },
  }
}

export type PortfolioEndpoints = ReturnType<typeof portfolioEndpoints>
